import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Copy, ExternalLink, TrendingUp, Users, DollarSign } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth/AuthProvider";
import { toast } from "sonner";

interface Influencer {
  id: string;
  handle: string;
  display_name: string;
  revshare_bps: number;
  active: boolean;
}

interface PromoCode {
  id: string;
  code: string;
  discount_type: "percent" | "amount";
  discount_value: number;
  duration: string;
  max_redemptions: number | null;
  redemption_count: number;
  expires_at: string | null;
  active: boolean;
}

interface ConversionRow {
  id: string;
  gross_amount_cents: number;
  payout_amount_cents: number;
  currency: string;
  occurred_at: string;
  payout_status: string;
}

const fmtMoney = (cents: number, ccy = "usd") =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: ccy.toUpperCase() }).format(cents / 100);

const Influencer = () => {
  const { session, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [inf, setInf] = useState<Influencer | null>(null);
  const [codes, setCodes] = useState<PromoCode[]>([]);
  const [conversions, setConversions] = useState<ConversionRow[]>([]);
  const [referralCount, setReferralCount] = useState(0);

  useEffect(() => {
    if (authLoading) return;
    if (!session) {
      setLoading(false);
      return;
    }
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, authLoading]);

  const load = async () => {
    setLoading(true);
    const { data: infRow } = await supabase
      .from("influencers")
      .select("id, handle, display_name, revshare_bps, active")
      .eq("user_id", session!.user.id)
      .maybeSingle();

    if (!infRow) {
      setInf(null);
      setLoading(false);
      return;
    }
    setInf(infRow as Influencer);

    const [{ data: codeRows }, { data: convRows }, { count }] = await Promise.all([
      supabase
        .from("promo_codes")
        .select("id, code, discount_type, discount_value, duration, max_redemptions, redemption_count, expires_at, active")
        .eq("influencer_id", (infRow as Influencer).id)
        .order("created_at", { ascending: false }),
      supabase
        .from("referral_conversions")
        .select("id, gross_amount_cents, payout_amount_cents, currency, occurred_at, payout_status")
        .eq("influencer_id", (infRow as Influencer).id)
        .order("occurred_at", { ascending: false })
        .limit(50),
      supabase
        .from("referrals")
        .select("id", { count: "exact", head: true })
        .eq("influencer_id", (infRow as Influencer).id),
    ]);

    setCodes((codeRows ?? []) as PromoCode[]);
    setConversions((convRows ?? []) as ConversionRow[]);
    setReferralCount(count ?? 0);
    setLoading(false);
  };

  const copyLink = (code: string) => {
    const url = `${window.location.origin}/pricing?ref=${code}`;
    void navigator.clipboard.writeText(url);
    toast.success("Referral link copied");
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex flex-col bg-background text-foreground">
        <SiteHeader />
        <main className="flex-1 mx-auto w-full max-w-4xl px-6 py-16 text-muted-foreground">Loading…</main>
        <SiteFooter />
      </div>
    );
  }

  if (!session) {
    return (
      <div className="min-h-screen flex flex-col bg-background text-foreground">
        <SiteHeader />
        <main className="flex-1 mx-auto w-full max-w-2xl px-6 py-20 text-center">
          <h1 className="text-3xl font-bold">Influencer Portal</h1>
          <p className="mt-3 text-muted-foreground">Sign in to view your referral codes and conversions.</p>
          <Button asChild className="mt-6"><Link to="/auth">Sign in</Link></Button>
        </main>
        <SiteFooter />
      </div>
    );
  }

  if (!inf) {
    return (
      <div className="min-h-screen flex flex-col bg-background text-foreground">
        <SiteHeader />
        <main className="flex-1 mx-auto w-full max-w-2xl px-6 py-20 text-center">
          <h1 className="text-3xl font-bold">Become a Wavlynq partner</h1>
          <p className="mt-3 text-muted-foreground">
            We work with creators in the dating, friendship, and mental-health space.
            Get a custom code, share it with your audience, and earn revenue share on every paid signup.
          </p>
          <Button asChild className="mt-6">
            <a href="mailto:partners@wavlynq.com?subject=Influencer%20application">
              Apply to partner program <ExternalLink className="ml-1.5 h-4 w-4" />
            </a>
          </Button>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const totalGross = conversions.reduce((s, c) => s + c.gross_amount_cents, 0);
  const totalPayout = conversions.reduce((s, c) => s + c.payout_amount_cents, 0);
  const pendingPayout = conversions
    .filter((c) => c.payout_status === "pending")
    .reduce((s, c) => s + c.payout_amount_cents, 0);

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      <SiteHeader />
      <main className="flex-1 mx-auto w-full max-w-5xl px-6 py-12 space-y-10">
        <header className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.24em] text-muted-foreground">Partner</p>
            <h1 className="mt-1 text-3xl font-bold">@{inf.handle}</h1>
            <p className="text-sm text-muted-foreground">{inf.display_name} · {inf.revshare_bps / 100}% revshare</p>
          </div>
          {!inf.active && <Badge variant="destructive">Paused</Badge>}
        </header>

        <section className="grid gap-4 sm:grid-cols-3">
          <Card className="p-5">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
              <Users className="h-3.5 w-3.5" /> Referrals
            </div>
            <p className="mt-2 text-2xl font-bold">{referralCount}</p>
          </Card>
          <Card className="p-5">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
              <TrendingUp className="h-3.5 w-3.5" /> Gross volume
            </div>
            <p className="mt-2 text-2xl font-bold">{fmtMoney(totalGross)}</p>
          </Card>
          <Card className="p-5">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
              <DollarSign className="h-3.5 w-3.5" /> Earned · pending
            </div>
            <p className="mt-2 text-2xl font-bold">{fmtMoney(totalPayout)}</p>
            <p className="text-xs text-muted-foreground">{fmtMoney(pendingPayout)} pending payout</p>
          </Card>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-3">Your codes</h2>
          {codes.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No codes yet. Email partners@wavlynq.com to get one minted.
            </p>
          ) : (
            <div className="space-y-3">
              {codes.map((c) => (
                <Card key={c.id} className="p-4 flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <p className="font-mono text-lg font-bold">{c.code}</p>
                    <p className="text-xs text-muted-foreground">
                      {c.discount_type === "percent"
                        ? `${c.discount_value}% off`
                        : fmtMoney(c.discount_value) + " off"}
                      {" · "}{c.duration}
                      {" · "}{c.redemption_count}{c.max_redemptions ? `/${c.max_redemptions}` : ""} redeemed
                      {c.expires_at && ` · expires ${new Date(c.expires_at).toLocaleDateString()}`}
                      {!c.active && " · inactive"}
                    </p>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => copyLink(c.code)} disabled={!c.active}>
                    <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy link
                  </Button>
                </Card>
              ))}
            </div>
          )}
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-3">Recent conversions</h2>
          {conversions.length === 0 ? (
            <p className="text-sm text-muted-foreground">No conversions yet.</p>
          ) : (
            <Card className="divide-y">
              {conversions.map((c) => (
                <div key={c.id} className="p-4 flex items-center justify-between text-sm">
                  <div>
                    <p className="font-medium">{fmtMoney(c.gross_amount_cents, c.currency)}</p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(c.occurred_at).toLocaleString()}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium">{fmtMoney(c.payout_amount_cents, c.currency)}</p>
                    <Badge variant={c.payout_status === "paid" ? "default" : "secondary"} className="text-[10px]">
                      {c.payout_status}
                    </Badge>
                  </div>
                </div>
              ))}
            </Card>
          )}
        </section>
      </main>
      <SiteFooter />
    </div>
  );
};

export default Influencer;
